<div class="policy-mat-container">

  <!-- Loading -->
  <div *ngIf="loading" class="policy-loading">
    <mat-spinner diameter="36"></mat-spinner>
    <span>Loading policy configuration&#8230;</span>
  </div>

  <!-- Error -->
  <div *ngIf="!loading && error" class="policy-error">
    <mat-icon color="warn">error_outline</mat-icon>
    <span>{{ error }}</span>
  </div>

  <!-- Content -->
  <div *ngIf="!loading && !error && resourcePolicy">
    <mat-tab-group
      animationDuration="150ms"
      [selectedIndex]="activeTabIndex"
      (selectedIndexChange)="onTabChange($event)">

      <!-- VM POLICY TAB -->
      <mat-tab label="VM Policy"
               *ngIf="hasVMPolicy() && (resourceType === 'vm' || resourceType === 'both')">
        <div class="tab-body">

          <section class="policy-section">
            <h6 class="section-label">Policy Information</h6>
            <div class="fields-row">
              <mat-form-field appearance="outline" class="field-wide">
                <mat-label>Policy Name</mat-label>
                <input matInput [(ngModel)]="vmPolicyName" placeholder="Enter VM policy name" required>
              </mat-form-field>
              <mat-form-field appearance="outline" class="field-wide">
                <mat-label>Description</mat-label>
                <textarea matInput [(ngModel)]="vmPolicyDescription" rows="2"
                          placeholder="Optional description"></textarea>
              </mat-form-field>
            </div>
          </section>

          <mat-divider></mat-divider>

          <section class="policy-section">
            <h6 class="section-label">Creation Policy</h6>
            <mat-radio-group class="options-group">
              <div *ngFor="let option of resourcePolicy.VMPolicy!.CreationPolicyOptions" class="option-row">
                <mat-radio-button [value]="option.optionName"
                  [checked]="option.optionValue === 'true'"
                  (change)="onPolicyOptionChange('vm', 'creation', option)">
                  {{ formatDescription(option) }}
                </mat-radio-button>
              </div>
            </mat-radio-group>
          </section>

          <mat-divider></mat-divider>

          <section class="policy-section">
            <h6 class="section-label">Daily Deletion Policy</h6>
            <mat-radio-group class="options-group">
              <div *ngFor="let option of resourcePolicy.VMPolicy!.DailyDeletionPolicyOptions" class="option-block">
                <mat-radio-button [value]="option.optionName"
                  [checked]="option.optionValue === 'true'"
                  (change)="onPolicyOptionChange('vm', 'daily', option)">
                  {{ sanitizeDescription(option) }}
                </mat-radio-button>
                <div *ngIf="option.optionValue === 'true' && option.parUnit != null" class="timing-row">
                  <mat-form-field appearance="outline" class="timing-num-field">
                    <mat-label>After</mat-label>
                    <input matInput type="number" [(ngModel)]="option.parVal" min="1" placeholder="30">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="timing-unit-field">
                    <mat-label>Unit</mat-label>
                    <mat-select [(ngModel)]="option.parUnit">
                      <mat-option value="minutes">Minutes</mat-option>
                      <mat-option value="hours">Hours</mat-option>
                      <mat-option value="days">Days</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <span class="timing-suffix">after session</span>
                </div>
                <div *ngIf="option.optionValue === 'true' && option.allResources?.length" class="resources-block">
                  <span class="resources-label">Resources to delete:</span>
                  <div class="resources-row">
                    <mat-checkbox *ngFor="let resource of option.allResources"
                      [checked]="option.resourcesToBeDeleted?.includes(resource)"
                      (change)="onResourceSelectionChange('vm', 'daily', option, resource, $event.checked)"
                      class="resource-chip">{{ resource }}</mat-checkbox>
                  </div>
                </div>
              </div>
            </mat-radio-group>
          </section>

          <mat-divider></mat-divider>

          <section class="policy-section">
            <h6 class="section-label">After Class End</h6>
            <mat-radio-group class="options-group">
              <div *ngFor="let option of resourcePolicy.VMPolicy!.ClassEndDeletionOptions" class="option-block">
                <mat-radio-button [value]="option.optionName"
                  [checked]="option.optionValue === 'true'"
                  (change)="onPolicyOptionChange('vm', 'classend', option)">
                  {{ sanitizeDescription(option) }}
                </mat-radio-button>
                <div *ngIf="option.optionValue === 'true' && option.parUnit != null" class="timing-row">
                  <mat-form-field appearance="outline" class="timing-num-field">
                    <mat-label>After</mat-label>
                    <input matInput type="number" [(ngModel)]="option.parVal" min="1" placeholder="1">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="timing-unit-field">
                    <mat-label>Unit</mat-label>
                    <mat-select [(ngModel)]="option.parUnit">
                      <mat-option value="minutes">Minutes</mat-option>
                      <mat-option value="hours">Hours</mat-option>
                      <mat-option value="days">Days</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <span class="timing-suffix">of class completion</span>
                </div>
                <div *ngIf="option.optionValue === 'true' && option.allResources?.length" class="resources-block">
                  <span class="resources-label">Resources to delete:</span>
                  <div class="resources-row">
                    <mat-checkbox *ngFor="let resource of option.allResources"
                      [checked]="option.resourcesToBeDeleted?.includes(resource)"
                      (change)="onResourceSelectionChange('vm', 'classend', option, resource, $event.checked)"
                      class="resource-chip">{{ resource }}</mat-checkbox>
                  </div>
                </div>
              </div>
            </mat-radio-group>
          </section>

          <div class="actions-bar">
            <button mat-stroked-button type="button" (click)="resetVMPolicy()">
              <mat-icon>refresh</mat-icon> Reset
            </button>
            <button mat-raised-button color="primary" type="button"
                    (click)="saveVMPolicy()" [disabled]="loading">
              <mat-spinner *ngIf="loading" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!loading">save</mat-icon>
              Save VM Policy
            </button>
          </div>

        </div>
      </mat-tab>

      <!-- SUBSCRIPTION POLICY TAB -->
      <mat-tab label="Subscription Policy"
               *ngIf="hasSubscriptionPolicy() && (resourceType === 'subscription' || resourceType === 'both')">
        <div class="tab-body">

          <section class="policy-section">
            <h6 class="section-label">Policy Information</h6>
            <div class="fields-row">
              <mat-form-field appearance="outline" class="field-wide">
                <mat-label>Policy Name</mat-label>
                <input matInput [(ngModel)]="subscriptionPolicyName"
                       placeholder="Enter subscription policy name" required>
              </mat-form-field>
              <mat-form-field appearance="outline" class="field-wide">
                <mat-label>Description</mat-label>
                <textarea matInput [(ngModel)]="subscriptionPolicyDescription" rows="2"
                          placeholder="Optional description"></textarea>
              </mat-form-field>
            </div>
          </section>

          <mat-divider></mat-divider>

          <section class="policy-section">
            <h6 class="section-label">Creation Policy</h6>
            <mat-radio-group class="options-group">
              <div *ngFor="let option of resourcePolicy.SubscriptionPolicy!.CreateOptions" class="option-row">
                <mat-radio-button [value]="option.optionName"
                  [checked]="option.optionValue === 'true'"
                  (change)="onPolicyOptionChange('subscription', 'creation', option)">
                  {{ formatDescription(option) }}
                </mat-radio-button>
              </div>
            </mat-radio-group>
          </section>

          <mat-divider></mat-divider>

          <section class="policy-section">
            <h6 class="section-label">Daily Deletion Policy</h6>
            <mat-radio-group class="options-group">
              <div *ngFor="let option of resourcePolicy.SubscriptionPolicy!.DailyDeletionPolicyOptions" class="option-block">
                <mat-radio-button [value]="option.optionName"
                  [checked]="option.optionValue === 'true'"
                  (change)="onPolicyOptionChange('subscription', 'daily', option)">
                  {{ sanitizeDescription(option) }}
                </mat-radio-button>
                <div *ngIf="option.optionValue === 'true' && option.parUnit != null" class="timing-row">
                  <mat-form-field appearance="outline" class="timing-num-field">
                    <mat-label>Every</mat-label>
                    <input matInput type="number" [(ngModel)]="option.parVal" min="1" placeholder="1">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="timing-unit-field">
                    <mat-label>Unit</mat-label>
                    <mat-select [(ngModel)]="option.parUnit">
                      <mat-option value="min">Minutes</mat-option>
                      <mat-option value="hours">Hours</mat-option>
                      <mat-option value="days">Days</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <span class="timing-suffix">after session</span>
                </div>
              </div>
            </mat-radio-group>
          </section>

          <mat-divider></mat-divider>

          <section class="policy-section">
            <h6 class="section-label">After Class End</h6>
            <mat-radio-group class="options-group">
              <div *ngFor="let option of resourcePolicy.SubscriptionPolicy!.ClassEndDeletionOptions" class="option-block">
                <mat-radio-button [value]="option.optionName"
                  [checked]="option.optionValue === 'true'"
                  (change)="onPolicyOptionChange('subscription', 'classend', option)">
                  {{ sanitizeDescription(option) }}
                </mat-radio-button>
                <div *ngIf="option.optionValue === 'true' && option.parUnit != null" class="timing-row">
                  <mat-form-field appearance="outline" class="timing-num-field">
                    <mat-label>After</mat-label>
                    <input matInput type="number" [(ngModel)]="option.parVal" min="1" placeholder="1">
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="timing-unit-field">
                    <mat-label>Unit</mat-label>
                    <mat-select [(ngModel)]="option.parUnit">
                      <mat-option value="min">Minutes</mat-option>
                      <mat-option value="hours">Hours</mat-option>
                      <mat-option value="days">Days</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <span class="timing-suffix">of class completion</span>
                </div>
              </div>
            </mat-radio-group>
          </section>

          <ng-container *ngIf="resourcePolicy.SubscriptionPolicy!.QuotaReached?.length">
            <mat-divider></mat-divider>
            <section class="policy-section">
              <h6 class="section-label">Quota Reached Policy</h6>
              <mat-radio-group class="options-group">
                <div *ngFor="let option of resourcePolicy.SubscriptionPolicy!.QuotaReached" class="option-block">
                  <mat-radio-button [value]="option.optionName"
                    [checked]="option.optionValue === 'true'"
                    (change)="onPolicyOptionChange('subscription', 'quota', option)">
                    {{ sanitizeDescription(option) }}
                  </mat-radio-button>
                  <div *ngIf="option.optionValue === 'true' && option.parUnit != null" class="timing-row">
                    <mat-form-field appearance="outline" class="timing-num-field">
                      <mat-label>Value</mat-label>
                      <input matInput type="number" [(ngModel)]="option.parVal" min="0" placeholder="100">
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="timing-unit-field">
                      <mat-label>Currency / Unit</mat-label>
                      <mat-select [(ngModel)]="option.parUnit">
                        <mat-option value="Rs">Rs</mat-option>
                        <mat-option value="USD">USD</mat-option>
                        <mat-option value="%">%</mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>
                </div>
              </mat-radio-group>
            </section>
          </ng-container>

          <ng-container *ngIf="resourcePolicy.SubscriptionPolicy!.NotificationIntimations?.length">
            <mat-divider></mat-divider>
            <section class="policy-section">
              <h6 class="section-label">Notification Intimations</h6>
              <p class="section-hint">Configure which teams receive alerts at each quota usage threshold.</p>
              <div class="table-scroll">
                <table class="notif-table mat-elevation-z1">
                  <thead>
                    <tr>
                      <th class="notif-team-col">Team</th>
                      <th *ngFor="let n of resourcePolicy.SubscriptionPolicy!.NotificationIntimations; let i = index"
                          class="notif-pct-col">
                        <mat-form-field appearance="outline" class="pct-field">
                          <mat-label>%</mat-label>
                          <input matInput type="number" [value]="n.percentage"
                                 (input)="onPercentageChange(i, $any($event.target).value)"
                                 min="0" max="100">
                        </mat-form-field>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let teamType of getAllTeamTypes()">
                      <td class="notif-team-col">{{ teamType }}</td>
                      <td *ngFor="let n of resourcePolicy.SubscriptionPolicy!.NotificationIntimations"
                          class="notif-check-col">
                        <mat-checkbox
                          [checked]="hasTeamInNotification(n.percentage, teamType)"
                          (change)="onNotificationTeamChange(n.percentage, teamType, $event.checked)">
                        </mat-checkbox>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </ng-container>

          <div class="actions-bar">
            <button mat-stroked-button type="button" (click)="resetSubscriptionPolicy()">
              <mat-icon>refresh</mat-icon> Reset
            </button>
            <button mat-raised-button color="primary" type="button"
                    (click)="saveSubscriptionPolicy()" [disabled]="loading">
              <mat-spinner *ngIf="loading" diameter="18" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!loading">save</mat-icon>
              Save Subscription Policy
            </button>
          </div>

        </div>
      </mat-tab>

    </mat-tab-group>
  </div>

  <!-- No policy message -->
  <div *ngIf="!loading && !error && resourcePolicy && !hasVMPolicy() && !hasSubscriptionPolicy()"
       class="no-policy-msg">
    <mat-icon>info_outline</mat-icon>
    <span>No policy configuration available.</span>
  </div>

</div>
